import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { registerSW } from 'virtual:pwa-register';
import { ThemeProvider } from './contexts/ThemeContext';
import App from './App';
import { ErrorFallback } from './components/ErrorFallback';
import { initSentry } from './sentry';
import './index.css';

// Initialise Sentry before React mounts so render-time errors are
// captured. No-ops when VITE_SENTRY_DSN is unset (e.g. local dev).
initSentry();

// PWA service worker — register silently (no "New version available"
// toast). Custom sw.ts calls skipWaiting() on install so updates
// activate without a user Reload that historically re-served a stale
// precached index.html. ChunkLoadError recovery below still covers
// the rare stale-chunk 404 case. Native Capacitor shells don't ship sw.js.
if (!Capacitor.isNativePlatform()) {
  registerSW({
    immediate: true,
    onRegisterError(err) {
      console.warn('[pwa] SW registration failed:', err);
    },
  });
}

// Chunk-load-error recovery.
//
// When a PWA-installed user has an old service worker that's cached an
// old index.html, the cached HTML references chunk filenames like
// `dashboard-abc123.js`. After a fresh deploy those filenames change
// (`dashboard-def456.js`) and the old ones may be deleted from the CDN
// — so the user's browser tries to fetch a chunk that returns 404 and
// React's lazy() throws a ChunkLoadError. Without this handler the
// error bubbles to the Sentry ErrorBoundary and the user sees the
// generic error fallback.
//
// Fix: catch the chunk-load failure (one-shot via sessionStorage so
// we don't infinite-loop on a genuinely broken deploy) and force a
// hard reload — bypasses the SW cache for the navigation, gets fresh
// index.html with the new chunk references.
//
// Triggers on both flavours of the error:
//   - Vite/Rollup: "Failed to fetch dynamically imported module"
//   - Webpack-style fallback: "ChunkLoadError"
function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('ChunkLoadError') ||
    msg.includes('Loading chunk') ||
    msg.includes('Importing a module script failed')
  );
}

function tryReloadOnce(): void {
  if (typeof window === 'undefined') return;
  const KEY = 'golfauth.chunk-reload';
  try {
    if (sessionStorage.getItem(KEY) === '1') {
      // Already reloaded — this is a second failure, surface it
      // instead of looping. Sentry ErrorBoundary will catch it.
      return;
    }
    sessionStorage.setItem(KEY, '1');
  } catch {
    /* sessionStorage can be unavailable in private-mode iOS Safari —
       fall through to the reload anyway; worst case the user reloads
       twice instead of once before the underlying issue clears. */
  }
  // Hard reload bypasses the SW navigation cache. The new HTML pulls
  // the current chunk hashes from the server.
  window.location.reload();
}

window.addEventListener('error', (event) => {
  if (isChunkLoadError(event.error ?? event.message)) {
    tryReloadOnce();
  }
});
window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason)) {
    tryReloadOnce();
  }
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <ThemeProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <App />
        </Router>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);